import type { ContentLang } from '@yoyoclub/shared';

/**
 * One zh/en pair as edited in a form (docs/prd-bilingual-content.md §4).
 * `autoLang` names the side that is still an unreviewed machine translation.
 */
export interface BilingualValue {
  zh: string;
  en: string;
  autoLang: ContentLang | null;
}

export const emptyBilingual = (): BilingualValue => ({ zh: '', en: '', autoLang: null });

const SIDE_KEY = { ZH: 'zh', EN: 'en' } as const;

/**
 * Replace one side's text with the same rules as typing into it (PRD §4.2):
 * editing a machine side makes it the host's own words, and changing the
 * source of an untouched machine translation clears that translation at once
 * (it is refilled on blur), so a save mid-edit never submits stale machine text.
 */
export function applySideEdit(
  value: BilingualValue,
  side: ContentLang,
  text: string
): BilingualValue {
  const other: ContentLang = side === 'ZH' ? 'EN' : 'ZH';
  const next: BilingualValue = { ...value, [SIDE_KEY[side]]: text };
  if (value.autoLang === side) next.autoLang = null;
  if (value.autoLang === other && text !== value[SIDE_KEY[side]]) {
    next[SIDE_KEY[other]] = '';
    next.autoLang = null;
  }
  return next;
}

export function toBilingualValue(row: {
  zh?: string | null;
  en?: string | null;
  autoLang?: ContentLang | null;
}): BilingualValue {
  return { zh: row.zh ?? '', en: row.en ?? '', autoLang: row.autoLang ?? null };
}

/** True when at least one side has text. */
export function hasBilingualValue(v: BilingualValue): boolean {
  return v.zh.trim() !== '' || v.en.trim() !== '';
}

export interface TitlePayload {
  titleCN: string | null;
  titleEN: string | null;
  titleAutoLang: ContentLang | null;
}
export interface DescriptionPayload {
  descriptionCN: string | null;
  descriptionEN: string | null;
  descriptionAutoLang: ContentLang | null;
}
export interface BioPayload {
  bioZh: string | null;
  bioEn: string | null;
  bioAutoLang: ContentLang | null;
}

/** Payload columns for the API (trimmed; empty becomes null). */
export function bilingualPayload(v: BilingualValue, kind: 'title'): TitlePayload;
export function bilingualPayload(v: BilingualValue, kind: 'description'): DescriptionPayload;
export function bilingualPayload(v: BilingualValue, kind: 'bio'): BioPayload;
export function bilingualPayload(
  v: BilingualValue,
  kind: 'title' | 'description' | 'bio'
): TitlePayload | DescriptionPayload | BioPayload {
  const zh = v.zh.trim() || null;
  const en = v.en.trim() || null;
  const autoLang = (v.autoLang === 'ZH' && zh) || (v.autoLang === 'EN' && en) ? v.autoLang : null;
  if (kind === 'title') return { titleCN: zh, titleEN: en, titleAutoLang: autoLang };
  if (kind === 'bio') return { bioZh: zh, bioEn: en, bioAutoLang: autoLang };
  return { descriptionCN: zh, descriptionEN: en, descriptionAutoLang: autoLang };
}
